import type {
  ApiKeyCreatedDTO,
  ApiKeyDTO,
  AuditEntryDTO,
  AuditExportDTO,
  InvitationDTO,
  MembershipDTO,
  OrganizationDTO,
  OrganizationStatsDTO,
  Page,
  PublicInvitationDTO,
  SessionDTO,
  UserDTO,
} from '@keystone/shared';
import { ApiProblem, isAbort } from './problem.js';

/**
 * The API client.
 *
 * ## Where the credentials live
 *
 * The access token is held in a **private field of this class and nowhere else**.
 * Not `localStorage`, not `sessionStorage`, not a module-level variable, not a
 * React context that a devtools extension can read, not a cookie the page can
 * touch. The consequence is that a full page reload signs the user out of the
 * *access* token while the refresh cookie survives, and the first request after
 * the reload silently exchanges it for a new one. That is the intended
 * behaviour: it is what makes an XSS payload unable to walk away with a
 * long-lived credential.
 *
 * The refresh token is an `HttpOnly` cookie the browser attaches to
 * `/api/auth/refresh` and to nothing else. This code cannot read it, which is
 * the point, and it never tries.
 *
 * ## Why refresh is single-flight
 *
 * A page like Overview fires four requests at once. If the access token has
 * expired, all four get a 401 at the same moment. Naively, all four then call
 * `/api/auth/refresh` — and because refresh tokens **rotate on every use and
 * reuse is treated as theft**, the second call presents a token the first call
 * already revoked. The API correctly concludes the family has leaked and revokes
 * every session, signing the user out of every device.
 *
 * So a refresh in progress is stored as a promise and every concurrent caller
 * awaits that same promise. One request is made, one rotation happens, and all
 * four original requests retry with the same new token. This is the single most
 * important behaviour in the file.
 *
 * ## Retry exactly once
 *
 * The retry after a successful refresh is not itself wrapped in a retry. A 401
 * that survives a fresh token is not an expiry problem — it is a revoked
 * session, a disabled account, or a bug — and retrying it would loop.
 */

/* -------------------------------------------------------------------------- */
/*  Wire types                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The body of register, login and refresh.
 *
 * Identical on all three paths, which is what lets the client treat "I just
 * signed in" and "I just rotated" as the same state transition.
 */
export interface AuthSession {
  user: UserDTO;
  accessToken: string;
  expiresIn: number;
  tokenType: 'Bearer';
}

/** An invitation plus the one-time token, returned only by the create endpoint. */
export interface InvitationCreatedDTO extends InvitationDTO {
  token: string;
  acceptUrl: string;
}

/** The result of a role change: what it was, and what it became. */
export interface RoleChangeResult {
  userId: string;
  role: string;
  previousRole: string;
}

export interface RawResult {
  status: number;
  statusText: string;
  durationMs: number;
  headers: Record<string, string>;
  body: string;
  contentType: string;
}

/* -------------------------------------------------------------------------- */
/*  Client                                                                    */
/* -------------------------------------------------------------------------- */

type QueryValue = string | number | boolean | undefined | null;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /** Send the access token. Default true; login and register set it false. */
  auth?: boolean;
  headers?: Record<string, string>;
  /**
   * `true` mints a fresh UUID; a string uses the caller's value.
   *
   * A string is what a retry button should pass: the whole point of an
   * idempotency key is that the *same* key is presented on the second attempt,
   * so a key generated inside the retry handler would defeat it.
   */
  idempotencyKey?: string | true;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export class ApiClient {
  #accessToken: string | null = null;
  #refreshInFlight: Promise<boolean> | null = null;
  #onAuthLost: (() => void) | null = null;

  /* ---------------------------------------------------------------------- */
  /*  Token lifecycle                                                       */
  /* ---------------------------------------------------------------------- */

  /**
   * Adopts an access token.
   *
   * Called on login, on register, and after every rotation. There is no
   * corresponding `persist` — the token is not written anywhere.
   */
  adoptAccessToken(token: string): void {
    this.#accessToken = token;
  }

  /** Drops the access token without touching the refresh cookie. */
  clearAccessToken(): void {
    this.#accessToken = null;
  }

  get hasAccessToken(): boolean {
    return this.#accessToken !== null;
  }

  /**
   * Registers the callback that runs when re-authentication is impossible.
   *
   * The client does not import the auth context — that would be a cycle, and it
   * would put React state inside a class that has to be usable outside a
   * component tree. Instead the context registers a setter here, and the client
   * calls it. One direction of dependency.
   */
  onAuthLost(callback: () => void): void {
    this.#onAuthLost = callback;
  }

  /* ---------------------------------------------------------------------- */
  /*  Refresh                                                               */
  /* ---------------------------------------------------------------------- */

  /**
   * Exchanges the refresh cookie for a new access token, at most once at a time.
   *
   * Resolves `true` when a new token was adopted, `false` when the session is
   * gone. It never rejects: every caller's next move is the same either way, and
   * making them all handle an exception to learn a boolean would be noise.
   */
  async refresh(): Promise<boolean> {
    // The latch. Assigning before awaiting is what makes this single-flight —
    // the second caller sees a promise, not a second request.
    this.#refreshInFlight ??= this.#performRefresh().finally(() => {
      this.#refreshInFlight = null;
    });
    return this.#refreshInFlight;
  }

  async #performRefresh(): Promise<boolean> {
    let response: Response;
    try {
      response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
    } catch {
      // A network failure is not an authentication failure. Keeping the token
      // means a flaky connection does not sign the user out, and the next
      // request will try again.
      return false;
    }

    if (!response.ok) {
      // Any rejection from the refresh endpoint is terminal: the cookie is gone,
      // expired, or belonged to a revoked family. The API clears the cookie on
      // every failure path, so there is nothing to preserve.
      this.#accessToken = null;
      this.#onAuthLost?.();
      return false;
    }

    const session = (await response.json()) as AuthSession;
    this.#accessToken = session.accessToken;
    return true;
  }

  /* ---------------------------------------------------------------------- */
  /*  Requests                                                              */
  /* ---------------------------------------------------------------------- */

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    // The body is serialised once, outside the attempt, so a retry after a
    // refresh sends byte-identical content. Re-serialising could reorder keys,
    // which would change an `Idempotency-Key` request hash and turn a legitimate
    // retry into a 409.
    const prepared = this.#prepare(path, options);

    try {
      return await this.#send<T>(prepared, options.signal);
    } catch (error) {
      // An abort arrives as a `DOMException` and a bug arrives as something else
      // entirely; neither is refreshable, and both are rethrown untouched.
      if (!(error instanceof ApiProblem)) throw error;

      const refreshable = error.status === 401 && options.auth !== false && this.#accessToken !== null;
      if (!refreshable) throw error;

      const refreshed = await this.refresh();
      if (!refreshed) throw error;

      // Exactly one retry. See the module comment.
      return this.#send<T>(prepared, options.signal);
    }
  }

  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  /**
   * A DELETE, which may carry a body.
   *
   * `DELETE /organizations/{slug}` requires the slug to be echoed back as
   * confirmation, so a body is not optional here even though many HTTP clients
   * make it awkward. `fetch` sends one without complaint.
   */
  delete<T = void>(path: string, options: Omit<RequestOptions, 'method'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  /**
   * A multipart upload.
   *
   * Separate from `request` because `FormData` must not be JSON-stringified and
   * because the `Content-Type` header has to be left alone: the browser sets it,
   * including the multipart boundary, and setting it by hand omits the boundary
   * and produces a body the server cannot parse.
   */
  async upload<T>(
    path: string,
    file: File,
    options: { field?: string; method?: 'POST' | 'PATCH' } = {},
  ): Promise<T> {
    const form = new FormData();
    form.append(options.field ?? 'file', file, file.name);

    const send = async (): Promise<T> => {
      const headers = new Headers({ Accept: 'application/json' });
      if (this.#accessToken) headers.set('Authorization', `Bearer ${this.#accessToken}`);

      let response: Response;
      try {
        response = await fetch(`${API_BASE}${path}`, {
          method: options.method ?? 'POST',
          headers,
          body: form,
          credentials: 'include',
        });
      } catch (error) {
        throw ApiProblem.network(error);
      }

      return this.#parse<T>(response);
    };

    try {
      return await send();
    } catch (error) {
      if (!(error instanceof ApiProblem) || error.status !== 401 || this.#accessToken === null) {
        throw error;
      }
      if (!(await this.refresh())) throw error;
      return send();
    }
  }

  /**
   * A request that returns the raw exchange rather than a parsed body.
   *
   * Used only by the "Try it" panel, whose entire purpose is to show what the
   * API actually sent back — including the status, the headers, the timing and a
   * body that is not JSON. Parsing it first would destroy exactly the information
   * the panel exists to display.
   */
  async raw(path: string, init: { method?: string; body?: string; idempotencyKey?: string } = {}): Promise<RawResult> {
    const headers = new Headers({ Accept: 'application/json' });
    if (init.body !== undefined && init.body.trim().length > 0) {
      headers.set('Content-Type', 'application/json');
    }
    if (init.idempotencyKey) headers.set('Idempotency-Key', init.idempotencyKey);
    if (this.#accessToken) headers.set('Authorization', `Bearer ${this.#accessToken}`);

    const started = performance.now();
    let response: Response;
    try {
      response = await fetch(`${API_BASE}${path}`, {
        method: init.method ?? 'GET',
        headers,
        body: init.body && init.body.trim().length > 0 ? init.body : undefined,
        credentials: 'include',
      });
    } catch (error) {
      const problem = ApiProblem.network(error);
      return {
        status: 0,
        statusText: problem.title,
        durationMs: Math.round(performance.now() - started),
        headers: {},
        body: JSON.stringify({ type: problem.type, title: problem.title, detail: problem.detail }, null, 2),
        contentType: 'application/json',
      };
    }

    const text = await response.text();
    const collected: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      collected[key] = value;
    });

    return {
      status: response.status,
      statusText: response.statusText,
      durationMs: Math.round(performance.now() - started),
      headers: collected,
      body: text,
      contentType: response.headers.get('Content-Type') ?? '',
    };
  }

  /* ---------------------------------------------------------------------- */
  /*  Internals                                                             */
  /* ---------------------------------------------------------------------- */

  /**
   * Everything that must not change between the first attempt and the retry.
   *
   * Computed once and reused. The URL, the serialised body and the idempotency
   * key all fall into this category: if any of them differed, the retry would be
   * a different request and the idempotency record would (correctly) reject it.
   */
  #prepare(
    path: string,
    options: RequestOptions,
  ): { url: string; method: string; body: string | undefined; headers: Headers } {
    // Resolved against the page origin so that a relative `path` works and an
    // absolute `API_BASE` still wins. `URL` also percent-encodes the query
    // values, which is what keeps a cursor containing `+` or `=` intact.
    const url = new URL(`${API_BASE}${path}`, window.location.origin);
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }

    const headers = new Headers({ Accept: 'application/json' });
    for (const [key, value] of Object.entries(options.headers ?? {})) headers.set(key, value);

    const body = options.body === undefined ? undefined : JSON.stringify(options.body);
    if (body !== undefined) headers.set('Content-Type', 'application/json');

    if (options.idempotencyKey) {
      headers.set(
        'Idempotency-Key',
        options.idempotencyKey === true ? crypto.randomUUID() : options.idempotencyKey,
      );
    }

    if (options.auth !== false && this.#accessToken !== null) {
      headers.set('Authorization', `Bearer ${this.#accessToken}`);
    }

    return { url: url.toString(), method: options.method ?? 'GET', body, headers };
  }

  async #send<T>(
    prepared: { url: string; method: string; body: string | undefined; headers: Headers },
    signal: AbortSignal | undefined,
  ): Promise<T> {
    let response: Response;
    try {
      response = await fetch(prepared.url, {
        method: prepared.method,
        headers: prepared.headers,
        body: prepared.body,
        // Required for the refresh cookie. Same-origin in development via the
        // Vite proxy, so this is the only value that works in both setups.
        credentials: 'include',
        ...(signal ? { signal } : {}),
      });
    } catch (error) {
      if (isAbort(error)) throw error;
      throw ApiProblem.network(error);
    }

    return this.#parse<T>(response);
  }

  async #parse<T>(response: Response): Promise<T> {
    // 204 has no body by definition, and calling `.json()` on it throws. Every
    // revoke endpoint returns 204, so this is the common case, not an edge case.
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const body: unknown = text.length > 0 ? safeJson(text) : null;

    if (!response.ok) throw ApiProblem.from(response, body, text);

    return body as T;
  }
}

/**
 * `JSON.parse` that returns `null` instead of throwing.
 *
 * A 200 with a body that is not JSON is a server bug, but it must not surface as
 * a `SyntaxError` from inside the response handler — that would be reported as an
 * unexplained client crash rather than "the API returned something unparseable".
 */
function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/*  Typed endpoint helpers                                                    */
/* -------------------------------------------------------------------------- */

/** The one client instance. A singleton because the token must be too. */
export const api = new ApiClient();

/**
 * Thin named wrappers over the client.
 *
 * They exist so that a path or a query-parameter name appears exactly once in
 * the codebase. The alternative — `api.get('/organizations/' + slug + '/members')`
 * scattered across seven page components — is how a rename becomes a search for
 * string literals.
 *
 * Every read takes an optional `AbortSignal` as its last argument. That is what
 * lets `useAsync` cancel a request when the reader navigates away, so a slow
 * response for the page they left cannot land after the page they are on.
 */
export const endpoints = {
  /* Authentication ------------------------------------------------------- */
  register: (body: { email: string; password: string; name: string }) =>
    api.post<AuthSession>('/api/auth/register', body, { auth: false }),
  login: (body: { email: string; password: string }) =>
    api.post<AuthSession>('/api/auth/login', body, { auth: false }),
  logout: () => api.post<void>('/api/auth/logout'),
  logoutAll: () => api.post<{ revoked: number }>('/api/auth/logout-all'),
  me: (signal?: AbortSignal) => api.get<UserDTO>('/api/auth/me', withSignal(signal)),
  myOrganizations: (signal?: AbortSignal) =>
    api.get<OrganizationDTO[]>('/api/me/organizations', withSignal(signal)),
  uploadAvatar: (file: File) => api.upload<UserDTO>('/api/me/avatar', file),

  /* Sessions ------------------------------------------------------------- */
  sessions: (query: { cursor?: string; limit?: number } = {}, signal?: AbortSignal) =>
    api.get<Page<SessionDTO>>('/api/auth/sessions', { query, ...withSignal(signal) }),
  revokeSession: (id: string) => api.delete<void>(`/api/auth/sessions/${id}`),

  /* Organisations -------------------------------------------------------- */
  organizations: (signal?: AbortSignal) =>
    api.get<OrganizationDTO[]>('/api/organizations', withSignal(signal)),
  organization: (slug: string, signal?: AbortSignal) =>
    api.get<OrganizationDTO>(`/api/organizations/${slug}`, withSignal(signal)),
  createOrganization: (body: { name: string; slug?: string }, idempotencyKey?: string) =>
    api.post<OrganizationDTO>('/api/organizations', body, {
      ...(idempotencyKey ? { idempotencyKey } : {}),
    }),
  updateOrganization: (slug: string, body: { name?: string; slug?: string }) =>
    api.patch<OrganizationDTO>(`/api/organizations/${slug}`, body),
  deleteOrganization: (slug: string, confirm: string) =>
    api.delete<void>(`/api/organizations/${slug}`, { body: { confirm } }),
  stats: (slug: string, signal?: AbortSignal) =>
    api.get<OrganizationStatsDTO>(`/api/organizations/${slug}/stats`, withSignal(signal)),
  transferOwnership: (slug: string, userId: string) =>
    api.post<OrganizationDTO>(`/api/organizations/${slug}/transfer-ownership`, { userId }),
  uploadLogo: (slug: string, file: File) =>
    api.upload<OrganizationDTO>(`/api/organizations/${slug}/logo`, file),

  /* Members -------------------------------------------------------------- */
  members: (slug: string, query: { cursor?: string; limit?: number } = {}, signal?: AbortSignal) =>
    api.get<Page<MembershipDTO>>(`/api/organizations/${slug}/members`, { query, ...withSignal(signal) }),
  updateMember: (slug: string, userId: string, role: string) =>
    api.patch<RoleChangeResult>(`/api/organizations/${slug}/members/${userId}`, { role }),
  removeMember: (slug: string, userId: string) =>
    api.delete<void>(`/api/organizations/${slug}/members/${userId}`),

  /* Invitations ---------------------------------------------------------- */
  invitations: (slug: string, query: { cursor?: string; limit?: number } = {}, signal?: AbortSignal) =>
    api.get<Page<InvitationDTO>>(`/api/organizations/${slug}/invitations`, {
      query,
      ...withSignal(signal),
    }),
  createInvitation: (slug: string, body: { email: string; role: string }, idempotencyKey?: string) =>
    api.post<InvitationCreatedDTO>(`/api/organizations/${slug}/invitations`, body, {
      ...(idempotencyKey ? { idempotencyKey } : {}),
    }),
  revokeInvitation: (slug: string, id: string) =>
    api.delete<void>(`/api/organizations/${slug}/invitations/${id}`),
  publicInvitation: (token: string, signal?: AbortSignal) =>
    api.get<PublicInvitationDTO>(`/api/invitations/${token}`, { auth: false, ...withSignal(signal) }),
  acceptInvitation: (token: string) =>
    api.post<MembershipDTO>(`/api/invitations/${token}/accept`),

  /* API keys ------------------------------------------------------------- */
  apiKeys: (slug: string, query: { cursor?: string; limit?: number } = {}, signal?: AbortSignal) =>
    api.get<Page<ApiKeyDTO>>(`/api/organizations/${slug}/api-keys`, { query, ...withSignal(signal) }),
  createApiKey: (
    slug: string,
    body: { name: string; scopes?: string[]; expiresInDays?: number },
    idempotencyKey?: string,
  ) =>
    api.post<ApiKeyCreatedDTO>(`/api/organizations/${slug}/api-keys`, body, {
      ...(idempotencyKey ? { idempotencyKey } : {}),
    }),
  revokeApiKey: (slug: string, id: string) =>
    api.delete<void>(`/api/organizations/${slug}/api-keys/${id}`),

  /* Audit ---------------------------------------------------------------- */
  audit: (
    slug: string,
    query: {
      cursor?: string;
      limit?: number;
      action?: string;
      entity?: string;
      entityId?: string;
      actorId?: string;
      from?: string;
      to?: string;
    } = {},
    signal?: AbortSignal,
  ) => api.get<Page<AuditEntryDTO>>(`/api/organizations/${slug}/audit`, { query, ...withSignal(signal) }),
  exportAudit: (slug: string, body: { from?: string; to?: string }) =>
    api.post<AuditExportDTO>(`/api/organizations/${slug}/audit/export`, body),
} as const;

/**
 * Spreads a signal into an options object only when one was supplied.
 *
 * `exactOptionalPropertyTypes` is off in this repo, so `{ signal: undefined }`
 * would be accepted — but it would also be a different object from one without
 * the key, and `fetch` treats an explicitly-undefined signal identically. Doing
 * it this way keeps the call sites uniform and the intent obvious.
 */
function withSignal(signal: AbortSignal | undefined): { signal?: AbortSignal } {
  return signal ? { signal } : {};
}
